// frontend/src/components/common/Profile.jsx

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import Card from './Card';
import Button from './Button';
import Input from './Input';
import SessionManager from './SessionManager';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('info'); // 'info' ou 'sessions'
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    avatarUrl: user?.avatarUrl || '',
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [avatarError, setAvatarError] = useState(false);

  const getEndpoint = () => {
    switch (user?.role) {
      case 'admin':
        return `/admins/${user.id}`;
      case 'user_owner':
        return '/owners/me';
      case 'user_simple':
        return '/simples/me';
      default:
        return null;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-blue-500';
      case 'user_owner':
        return 'bg-green-500';
      case 'user_simple':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getRoleName = (role) => {
    switch (role) {
      case 'admin':
        return 'Administrateur';
      case 'user_owner':
        return 'User Owner';
      case 'user_simple':
        return 'User Simple';
      default:
        return role;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    setLoading(true);

    try {
      const endpoint = getEndpoint();
      const response = await api.put(endpoint, formData);

      updateUser({
        ...user,
        name: formData.name,
        email: formData.email,
        avatarUrl: formData.avatarUrl,
      });

      setMessage({ type: 'success', text: 'Profil mis à jour avec succès !' });
      setAvatarError(false);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erreur lors de la mise à jour',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">Mon Profil</h1>

      {/* Onglets */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('info')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'info'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📋 Informations personnelles
          </button>
          <button
            onClick={() => setActiveTab('sessions')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'sessions'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            💻 Appareils connectés
          </button>
        </nav>
      </div>

      {/* Contenu des onglets */}
      {activeTab === 'info' ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Avatar et infos */}
          <Card className="md:col-span-1">
            <div className="text-center">
              {/* Avatar avec image ou initiale */}
              {formData.avatarUrl && !avatarError ? (
                <img
                  src={formData.avatarUrl}
                  alt={user?.name}
                  className="w-24 h-24 mx-auto rounded-full object-cover mb-4 border-4 border-gray-200"
                  onError={() => setAvatarError(true)}
                />
              ) : (
                <div
                  className={`w-24 h-24 mx-auto rounded-full ${getRoleColor(
                    user?.role
                  )} text-white flex items-center justify-center text-4xl font-bold mb-4`}
                >
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
              )}

              <h2 className="text-xl font-bold text-gray-800">{user?.name}</h2>
              <p className="text-sm text-gray-500">{user?.email}</p>
              <div className="mt-4">
                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                  {getRoleName(user?.role)}
                </span>
              </div>
            </div>
          </Card>

          {/* Formulaire */}
          <Card className="md:col-span-2" title="Modifier mes informations">
            {message.text && (
              <div
                className={`mb-4 p-4 rounded-lg ${
                  message.type === 'success'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <Input
                label="Nom complet"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />

              <Input
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
              />

              <Input
                label="URL de l'avatar (optionnel)"
                type="url"
                value={formData.avatarUrl}
                onChange={(e) => {
                  setFormData({ ...formData, avatarUrl: e.target.value });
                  setAvatarError(false);
                }}
                placeholder="https://example.com/avatar.jpg"
              />

              <div className="mt-6">
                <Button type="submit" disabled={loading} className="w-full">
                  {loading
                    ? 'Enregistrement...'
                    : 'Enregistrer les modifications'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      ) : (
        <SessionManager />
      )}
    </div>
  );
};

export default Profile;